<template>
  <div class="dashboard flex flex-col md:flex-row min-h-screen bg-gray-100 dark:bg-gray-900">
    <!-- Mobile Tab Navigation -->
    <div class="md:hidden bg-white dark:bg-gray-800 shadow-md">
      <div class="flex items-center justify-between p-4">
        <h2 class="text-xl font-semibold text-gray-800 dark:text-white">Dashboard</h2>
        <button @click="showMobileNav = !showMobileNav" class="text-gray-700 dark:text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      <nav v-if="showMobileNav" class="border-t border-gray-200 dark:border-gray-700">
        <a href="#" @click.prevent="activeTab = 'posts'; showMobileNav = false" class="block px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" :class="{ 'bg-gray-200 dark:bg-gray-700': activeTab === 'posts' }">
          ブログ記事
        </a>
        <a href="#" @click.prevent="activeTab = 'settings'; showMobileNav = false" class="block px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" :class="{ 'bg-gray-200 dark:bg-gray-700': activeTab === 'settings' }">
          設定
        </a>
      </nav>
    </div>

    <!-- Desktop Left Sidebar -->
    <div class="hidden md:block w-64 bg-white dark:bg-gray-800 shadow-md">
      <div class="p-4">
        <h2 class="text-xl font-semibold text-gray-800 dark:text-white">Dashboard</h2>
      </div>
      <nav class="mt-4">
        <a href="#" @click.prevent="activeTab = 'posts'" class="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" :class="{ 'bg-gray-200 dark:bg-gray-700': activeTab === 'posts' }">
          ブログ記事
        </a>
        <a href="#" @click.prevent="activeTab = 'settings'" class="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700" :class="{ 'bg-gray-200 dark:bg-gray-700': activeTab === 'settings' }">
          設定
        </a>
      </nav>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-y-auto">
      <div class="p-8">
        <div v-if="isAuthenticated">
          <!-- Welcome Section -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h2 class="text-2xl font-semibold mb-4 text-gray-700 dark:text-gray-300">ようこそ、{{ user.username }}さん</h2>
          </div>

          <!-- Blog Posts Tab -->
          <div v-if="activeTab === 'posts'">
            <div class="flex justify-between items-center mb-6">
              <div>
                <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-300">あなたの記事</h3>
                <div class="mt-2 flex space-x-4">
                  <button
                    @click="currentView = 'published'"
                    class="px-4 py-2 rounded transition-colors duration-200"
                    :class="currentView === 'published' 
                      ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white'"
                  >
                    公開済み
                  </button>
                  <button
                    @click="currentView = 'drafts'"
                    class="px-4 py-2 rounded transition-colors duration-200"
                    :class="currentView === 'drafts' 
                      ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white'"
                  >
                    下書き
                  </button>
                </div>
              </div>
              <div class="flex space-x-2">
                 <button
                  @click="createNewPost"
                  :disabled="loading"
                  class="font-bold py-2 px-4 rounded border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  新規投稿
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-4">
              <p class="text-gray-600 dark:text-gray-400">読み込み中...</p>
            </div>
            <div v-else-if="filteredPosts.length === 0" class="text-center py-4">
              <p class="text-gray-600 dark:text-gray-400">
                {{ currentView === 'drafts' ? '下書きはありません。' : '投稿はありません。' }}
              </p>
            </div>
            <div v-else class="space-y-4">
              <!-- Drafts View: Iterate over individual drafts -->
              <template v-if="currentView === 'drafts'">
                <div v-for="draft in filteredPosts" :key="draft._id" class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-4">
                  <div class="flex justify-between items-start">
                    <div>
                      <h4 class="text-lg font-semibold text-gray-800 dark:text-white">{{ draft.title || '無題の記事' }}</h4>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        保存日時: {{ formatDate(draft.createdAt) }}
                        <span v-if="draft.isCurrent" class="ml-2 text-green-600 dark:text-green-400 font-semibold">(編集中)</span>
                      </p>
                       <p v-if="!draft.placeholder" class="text-xs text-gray-500 dark:text-gray-500">Draft ID: {{ draft._id }}</p>
                       <p v-if="!draft.placeholder" class="text-xs text-gray-500 dark:text-gray-500">Post ID: {{ draft.postId }}</p>
                    </div>
                    <div class="flex flex-col space-y-2">
                      <div class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">
                        下書き
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 flex space-x-2">
                    <!-- Edit button always goes to the main post editor -->
                    <button @click="editPost(draft.postId)" class="font-bold py-1 px-2 rounded text-sm border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white dark:border-yellow-400 dark:text-yellow-400 dark:hover:bg-yellow-400 dark:hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      編集
                    </button>
                    <!-- Publish button uses the specific draft ID -->
                    <button @click="publishDraft(draft.postId, draft._id)" class="font-bold py-1 px-2 rounded text-sm border border-green-600 text-green-600 hover:bg-green-600 hover:text-white dark:border-green-500 dark:text-green-500 dark:hover:bg-green-500 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" :disabled="!draft.title || draft.placeholder">
                      公開
                    </button>
                    <!-- Delete button targets the specific draft -->
                    <button @click="confirmDeleteDraft(draft._id)" class="font-bold py-1 px-2 rounded text-sm border border-red-500 text-red-500 hover:bg-red-500 hover:text-white dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" :disabled="draft.placeholder">
                      削除 (この下書き)
                    </button>
                  </div>
                </div>
              </template>

              <!-- Published View: Iterate over posts -->
              <template v-else>
                 <div v-for="post in filteredPosts" :key="post.id" class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-4">
                  <div class="flex justify-between items-start">
                    <div>
                      <h4 class="text-lg font-semibold text-gray-800 dark:text-white">{{ post.title }}</h4>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">公開日: {{ formatDate(post.publishedAt || post.createdAt) }}</p>
                       <p class="text-xs text-gray-500 dark:text-gray-500">Post ID: {{ post.id }}</p>
                    </div>
                    <div class="flex flex-col space-y-2">
                      <div class="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                        公開済み
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 flex space-x-2">
                    <button @click="viewPost(post.id)" class="font-bold py-1 px-2 rounded text-sm border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      表示
                    </button>
                    <button @click="editPost(post.id)" class="font-bold py-1 px-2 rounded text-sm border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white dark:border-yellow-400 dark:text-yellow-400 dark:hover:bg-yellow-400 dark:hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      編集
                    </button>
                    <!-- Archive button might not make sense for published posts, TBD -->
                    <!-- <button @click="archivePost(post)" class="font-bold py-1 px-2 rounded text-sm border border-gray-500 text-gray-500 hover:bg-gray-500 hover:text-white dark:border-gray-400 dark:text-gray-400 dark:hover:bg-gray-400 dark:hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      {{ post.archived ? '復元' : 'アーカイブ' }}
                    </button> -->
                    <button @click="confirmDeletePost(post.id)" class="font-bold py-1 px-2 rounded text-sm border border-red-500 text-red-500 hover:bg-red-500 hover:text-white dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                      削除
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Settings Tab -->
          <div v-if="activeTab === 'settings'">
            <h3 class="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-300">設定</h3>
            <p class="text-gray-600 dark:text-gray-400">設定機能は準備中です。</p>
          </div>
        </div>
        <div v-else>
          <p class="text-center text-gray-600 dark:text-gray-400">ダッシュボードにアクセスするにはログインしてください。</p>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
      <div class="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-lg font-bold mb-2 text-gray-800 dark:text-white">削除の確認</h3>
        <p v-if="itemToDelete.type === 'post'" class="text-gray-700 dark:text-gray-300 mb-4">この記事（および全てのドラフト）を削除してもよろしいですか？</p>
        <p v-else-if="itemToDelete.type === 'draft'" class="text-gray-700 dark:text-gray-300 mb-4">この下書きバージョンを削除してもよろしいですか？ (最後のドラフトは削除できません)</p>
        <div class="flex justify-end space-x-2">
          <button @click="cancelDelete" class="font-bold py-2 px-4 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors duration-200">
            キャンセル
          </button>
          <button @click="executeDelete" class="font-bold py-2 px-4 rounded border border-red-500 text-red-500 hover:bg-red-500 hover:text-white dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
            削除
          </button>
        </div>
      </div>
    </div>

    <!-- Notification -->
    <div v-if="notification" :class="[
      'fixed bottom-4 right-4 p-4 rounded-lg shadow-lg',
      notificationType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    ]">
      {{ notification }}
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { formatDate } from '@/utils/date'

export default {
  name: 'Dashboard',
  setup() {
    const store = useStore()
    const router = useRouter()
    const loading = ref(false)
    const showDeleteModal = ref(false)
    const itemToDelete = ref({ id: null, type: null }) // { id: postId or draftId, type: 'post' or 'draft' }
    const notification = ref('')
    const notificationType = ref('success')
    const activeTab = ref('posts')
    const currentView = ref('published') // Default to published
    const showMobileNav = ref(false)

    // currentViewの変更を監視してポストを再取得
    watch(currentView, async () => {
      await fetchUserPosts()
    })

    const user = computed(() => store.state.user)
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    // Use the getter for dashboard posts (which now includes allDrafts)
    const dashboardPosts = computed(() => store.getters.getDashboardPosts)

    const filteredPosts = computed(() => {
      if (currentView.value === 'drafts') {
        // Flatten the allDrafts arrays into a single list of draft objects
        return dashboardPosts.value.flatMap(post => {
          // Ensure allDrafts exists and is an array
          if (!Array.isArray(post.allDrafts) || post.allDrafts.length === 0) {
             // If a post object itself represents a draft but has no allDrafts (e.g., newly created), create a placeholder
             // This might happen if the backend enhancement failed or for a brand new post before first save.
             // We still want to show something for the user to edit.
             if (post.isDraft) {
                console.warn(`Post ${post.id} is a draft but has no allDrafts array. Creating placeholder.`);
                return [{
                  _id: post.id + '-current', // Use post ID + suffix for key
                  title: post.title || '無題の記事',
                  createdAt: post.lastSavedAt || post.updatedAt || post.createdAt,
                  isCurrent: true, // Assume it's the current one
                  postId: post.id, // Link back to the post
                  author: post.author,
                  isPostDraft: post.isDraft, // Keep original post status
                  placeholder: true // Flag it
                }];
             }
             return []; // Skip non-draft posts with no drafts
          }
          // Map each draft in the allDrafts array
          return post.allDrafts.map(draft => ({
            ...draft, // Spread draft properties (_id, title, createdAt, isCurrent)
            // Correctly use postId from the draft object itself
            postId: draft.postId || post._id, // Prefer draft.postId, fallback needed if backend is inconsistent
            postCreatedAt: post.createdAt, // Add original post creation date
            author: post.author, // Add author info (already populated in backend)
            isPostDraft: post.isDraft // Keep the original post's draft status
          }));
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort all drafts by their creation date
      } else {
        // For 'published' view, filter out posts marked as drafts
        return dashboardPosts.value.filter(post => !post.isDraft);
      }
    })

    onMounted(async () => {
      if (isAuthenticated.value) {
        await fetchUserPosts(); // Fetch initial data based on default view
      } else {
        router.push('/login');
      }
    });

    const logout = () => {
      store.dispatch('logout')
      router.push('/login')
      showNotification('ログアウトしました')
    }

    const fetchUserPosts = async () => {
      loading.value = true
      try {
        // Always fetch based on the current view ('published' or 'drafts')
        await store.dispatch('fetchUserPosts', currentView.value)
      } catch (error) {
        console.error('Error fetching user posts:', error)
        showNotification('記事の取得に失敗しました。もう一度お試しください。', 'error')
      } finally {
        loading.value = false
      }
    }

    // Renamed for clarity, accepts postId and draftId
    const publishDraft = async (postId, draftId) => {
       if (!postId || !draftId) {
         console.error('Cannot publish: Missing postId or draftId');
         showNotification('公開に必要な情報が不足しています。', 'error');
         return;
       }
       // Disable button if draft has no title (added :disabled in template)
       // const draftToPublish = filteredPosts.value.find(d => d._id === draftId);
       // if (!draftToPublish || !draftToPublish.title) {
       //    showNotification('タイトルがない下書きは公開できません。', 'error');
       //    return;
       // }

      try {
        await store.dispatch('publishPost', { postId, draftId });
        // After publishing, switch view to 'published' and refresh
        currentView.value = 'published';
        await fetchUserPosts();
        showNotification('記事を公開しました');
      } catch (error) {
        console.error('Error publishing post:', error);
        showNotification(error.response?.data?.message || '記事の公開に失敗しました。', 'error');
      }
    }

    const viewPost = (postId) => {
      router.push({ name: 'BlogPost', params: { id: postId } })
    }

    // Edit always goes to the main post editor using the postId
    const editPost = (postId) => {
      if (!postId) {
         console.error('Cannot edit: Missing postId');
         return;
      }
      // Extract the actual post ID if a placeholder key was used
      const actualPostId = postId.includes('-current') ? postId.replace('-current', '') : postId;
      router.push({ name: 'EditPost', params: { id: actualPostId } })
    }

     const createNewPost = async () => {
      loading.value = true; // Indicate loading
      try {
        // Dispatch createPost action (pass empty object or initial data if needed)
        const postId = await store.dispatch('createPost', { title: '', content: '' });
        if (postId) {
          // Navigate to the edit page for the new post
          router.push({ name: 'EditPost', params: { id: postId } });
        } else {
          throw new Error("Failed to get new post ID from store action.");
        }
      } catch (error) {
        console.error('Error creating new post:', error);
        showNotification('新規投稿の作成に失敗しました。', 'error');
      } finally {
        loading.value = false;
      }
    };

    // Archive is likely not applicable directly to drafts, maybe hide button?
    // Keeping it for published posts for now.
    const archivePost = async (post) => {
      try {
        await store.dispatch('archivePost', post) // Assuming this action exists
        await fetchUserPosts()
        showNotification(`記事を${post.archived ? '復元' : 'アーカイブ'}しました`)
      } catch (error) {
        console.error('Error archiving/unarchiving post:', error)
        showNotification('操作に失敗しました。もう一度お試しください。', 'error')
      }
    }

    // Confirm delete for a POST
    const confirmDeletePost = (postId) => {
       if (!postId) {
         console.error('Cannot delete post: Missing postId');
         return;
      }
      itemToDelete.value = { id: postId, type: 'post' };
      showDeleteModal.value = true
    }

    // Confirm delete for a DRAFT
    const confirmDeleteDraft = (draftId) => {
       if (!draftId) {
         console.error('Cannot delete draft: Missing draftId');
         return;
      }
       // Prevent deleting placeholder drafts
       if (draftId.includes('-current')) {
          showNotification('この下書きは削除できません。', 'error');
          return;
       }
      itemToDelete.value = { id: draftId, type: 'draft' };
      showDeleteModal.value = true
    }

    const cancelDelete = () => {
      itemToDelete.value = { id: null, type: null };
      showDeleteModal.value = false
    }

    // Execute delete based on type
    const executeDelete = async () => {
      if (!itemToDelete.value.id || !itemToDelete.value.type) return;

      const { id, type } = itemToDelete.value;

      try {
        if (type === 'post') {
          await store.dispatch('deletePost', id);
          showNotification('記事（および全てのドラフト）を削除しました');
        } else if (type === 'draft') {
          await store.dispatch('deleteDraftVersion', id);
          showNotification('下書きバージョンを削除しました');
        }
        await fetchUserPosts(); // Refresh the current view
      } catch (error) {
        console.error(`Error deleting ${type}:`, error);
        showNotification(error.response?.data?.message || '削除に失敗しました。もう一度お試しください。', 'error');
      } finally {
        cancelDelete();
      }
    }

    const showNotification = (message, type = 'success') => {
      notification.value = message
      notificationType.value = type
      setTimeout(() => {
        notification.value = ''
      }, 3000)
    }

    return {
      user,
      isAuthenticated,
      // dashboardPosts, // No longer directly used in template loop
      filteredPosts, // This now holds either published posts or flattened drafts
      loading,
      showDeleteModal,
      itemToDelete, // Expose for modal text
      notification,
      notificationType,
      activeTab,
      currentView,
      showMobileNav,
      logout,
      viewPost,
      editPost,
      publishDraft, // Renamed function
      archivePost,
      confirmDeletePost,
      confirmDeleteDraft, // New confirmation
      cancelDelete,
      executeDelete, // Renamed delete function
      formatDate,
      createNewPost
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here if needed */
</style>
